"use client";

import { useState, useEffect, FormEvent } from "react";
import Link from "next/link";
import { MessageSquare, ArrowRight, TrendingUp, BarChart3, Search, Mail, Video, CheckCircle, Loader2, BookOpen } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import type { ResourceMetadata } from "@/lib/resources";

interface ResourcesHubClientProps {
  allResources: ResourceMetadata[];
}

export function ResourcesHubClient({ allResources }: ResourcesHubClientProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formOpenTime, setFormOpenTime] = useState<number>(0);

  useEffect(() => {
    setFormOpenTime(Date.now());
  }, []);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const formData = new FormData(e.currentTarget);
    
    // Anti-spam: Check honeypot field (should be empty)
    const honeypot = formData.get("organization") as string;
    if (honeypot) {
      // Bot detected - fail silently
      console.warn("Spam detected: honeypot filled");
      setIsSuccess(true); // Don't give bots feedback
      setIsSubmitting(false);
      return;
    }
    
    // Anti-spam: Check time-based validation
    const timeSpent = Date.now() - formOpenTime;
    if (timeSpent < 3000) {
      console.warn("Spam detected: form submitted too quickly");
      setErrorMessage("Please take a moment to fill out the form.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/api/contact/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          formOpenTime,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to subscribe. Please try again later.");
      }

      setIsSuccess(true);
      setEmail("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredResources = allResources.filter(r => 
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [
    { 
      name: "Comparison", 
      href: "/resources/comparison", 
      icon: <BarChart3 className="h-6 w-6" />, 
      count: allResources.filter(r => r.type === 'benchmark').length,
      description: "Industry standards and efficiency data."
    },
    { 
      name: "Blog", 
      href: "/resources/blog", 
      icon: <MessageSquare className="h-6 w-6" />, 
      count: allResources.filter(r => r.type === 'blog').length,
      description: "Insights on eCommerce operations."
    },
    { 
      name: "Use Cases", 
      href: "/resources/use-cases", 
      icon: <TrendingUp className="h-6 w-6" />, 
      count: allResources.filter(r => r.type === 'use-case').length,
      description: "Real-world success stories."
    },
    {
      name: "Guides",
      href: "/resources/guides",
      icon: <BookOpen className="h-6 w-6" />,
      count: allResources.filter(r => r.type === 'guide').length,
      description: "Complete playbooks for AI-driven store management."
    },
    {
      name: "Videos",
      href: "/resources/videos",
      icon: <Video className="h-6 w-6" />,
      count: 0,
      description: "Product walkthroughs and strategy."
    },
  ];

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-purple-500/5 blur-[120px] -z-10" />
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-24">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight bg-gradient-to-b from-white to-slate-500 bg-clip-text text-transparent">
              Resources
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-12">
              Deep dives, comparisons, and guides to help you master your store's data and drive efficiency.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto group">
              <div className="absolute inset-0 bg-purple-500/20 blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity -z-10" />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
              <input 
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50 transition-all"
              />
            </div>
          </ScrollReveal>
        </div>

        {/* Categories */}
        {!searchQuery && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-32">
            {categories.map((cat, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <Link href={cat.href} className="group relative p-8 rounded-3xl bg-slate-900/50 border border-slate-800 hover:border-purple-500/50 transition-all flex flex-col h-full overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-[40px] -z-10 group-hover:bg-purple-500/10 transition-colors" />
                  
                  <div className="p-4 rounded-2xl bg-slate-950 text-purple-400 w-fit mb-6 group-hover:scale-110 transition-transform border border-slate-800">
                    {cat.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">{cat.name}</h3>
                  <p className="text-slate-400 mb-6 flex-grow">{cat.description}</p>
                  
                  <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-800/50">
                    <span className="text-sm font-medium text-slate-500">{cat.count} articles</span>
                    <div className="flex items-center text-sm font-bold text-purple-400 group-hover:translate-x-1 transition-transform">
                      Explore <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        )}

        {/* Latest Resources */}
        <div className="max-w-6xl mx-auto mb-32">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold">
                {searchQuery ? `Search Results (${filteredResources.length})` : "Latest blogs"}
              </h2>
              {!searchQuery && (
                <div className="h-px flex-grow mx-8 bg-slate-800/50 hidden md:block" />
              )}
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            {filteredResources.length > 0 ? (
              filteredResources.map((resource, i) => (
                <ScrollReveal key={i} delay={i * 100}>
                  <Link href={`/resources/${resource.type === 'use-case' ? 'use-cases' : resource.type === 'benchmark' ? 'comparison' : resource.type === 'guide' ? 'guides' : 'blog'}/${resource.slug}`}>
                    <div className="group p-8 rounded-[2rem] bg-slate-900/40 border border-slate-800/50 hover:border-purple-500/40 transition-all h-full flex flex-col">
                      <div className="flex items-center justify-between mb-6">
                        <span className="text-sm font-medium text-slate-500">{resource.date}</span>
                        <span className="text-xs font-black uppercase tracking-widest text-purple-400 bg-purple-400/10 px-3 py-1 rounded-full border border-purple-400/20">
                          {resource.type}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 group-hover:text-purple-400 transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-slate-400 mb-6 line-clamp-2 flex-grow">
                        {resource.summary}
                      </p>
                      <div className="flex items-center text-sm font-bold text-purple-400">
                        Read More <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))
            ) : (
              <div className="col-span-full text-center py-20 bg-slate-900/20 rounded-3xl border border-slate-800 border-dashed">
                <p className="text-slate-500">No resources found matching your search.</p>
              </div>
            )}
          </div>
        </div>

        {/* KPI Comparison Section */}
        {/* <div className="max-w-6xl mx-auto mb-32">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-bold mb-6">Key eCommerce KPI Comparisons (2025 Averages)</h2>
              <p className="text-xl md:text-2xl text-slate-400">How does your store compare to the industry standard?</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: "Average Order Value (AOV)", value: "$85", trend: "Stable" },
                { label: "Conversion Rate", value: "2.5%", trend: "Rising" },
                { label: "Customer Acquisition Cost (CAC)", value: "$22", trend: "Rising" },
                { label: "Repeat Purchase Rate", value: "28%", trend: "Stable" },
                { label: "Cart Abandonment Rate", value: "69%", trend: "Falling" },
                { label: "Refund Rate", value: "8%", trend: "Stable" },
                { label: "Gross Margin", value: "42%", trend: "Stable" },
                { label: "Net Profit Margin", value: "12%", trend: "Rising" }
              ].map((kpi, i) => (
                <div key={i} className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800 text-center">
                  <div className="text-base text-slate-500 mb-2">{kpi.label}</div>
                  <div className="text-3xl font-bold text-white mb-1">{kpi.value}</div>
                  <div className={`text-sm font-bold uppercase tracking-widest ${
                    kpi.trend === 'Rising' ? 'text-green-400' : kpi.trend === 'Falling' ? 'text-red-400' : 'text-slate-500'
                  }`}>
                    {kpi.trend}
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div> */}

        {/* Newsletter Section */}
        <ScrollReveal>
          <div className="max-w-5xl mx-auto relative">
            <div className="absolute inset-0 bg-purple-600/10 blur-[100px] -z-10" />
            <div className="p-12 md:p-20 rounded-[3rem] bg-slate-900/50 border border-slate-800 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Mail className="h-32 w-32 text-purple-400" />
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold mb-6">Stay ahead of the curve</h2>
              <p className="text-2xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Get the latest comparisons, operational insights, and eCommerce strategies delivered straight to your inbox.
              </p>
              
              {isSuccess ? (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-8 max-w-lg mx-auto animate-scale-in">
                  <div className="flex justify-center mb-4">
                    <CheckCircle className="h-12 w-12 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
                  <p className="text-slate-400">
                    Thank you for subscribing. We'll be in touch with the latest insights soon.
                  </p>
                  <button 
                    onClick={() => setIsSuccess(false)}
                    className="mt-6 text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  >
                    Subscribe another email
                  </button>
                </div>
              ) : (
                <>
                  <form className="flex flex-col md:flex-row gap-4 max-w-lg mx-auto" onSubmit={handleSubscribe}>
                    {/* Honeypot field - hidden from users */}
                    <div className="sr-only">
                      <label htmlFor="organization">Organization</label>
                      <input
                        id="organization"
                        name="organization"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <input 
                      type="email" 
                      required
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                      className="flex-grow bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-50"
                    />
                    <button 
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-4 rounded-2xl bg-purple-600 hover:bg-purple-500 text-white font-bold transition-all hover:scale-105 active:scale-95 shadow-xl shadow-purple-500/20 text-lg flex items-center justify-center min-w-[140px] disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        "Subscribe"
                      )}
                    </button>
                  </form>
                  {errorMessage && (
                    <p className="mt-4 text-red-400 font-medium animate-fade-in">{errorMessage}</p>
                  )}
                </>
              )}
              <p className="mt-6 text-base text-slate-500">
                Join a growing community of eCommerce operators. No spam, ever.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
